// CourseListPage.jsx - Complete working version with TA support
import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../Context";
import FileManagePage from "./FileManagePage";
import Blocked from "../components/Blocked";
import JoinPage from "./JoinPage";
import RosterPage from "./RosterPage";
import CourseAddPage from "./CourseAddPage";
import TAManagementDialog from "./TAManagement";
import LabSectionConfigModal from "./LabSectionConfigModal";
import { PostOptions } from "../utils/RequestUtils";
import { getApiUrl } from "../utils/api";
import axios from "axios";
import {
  FiPlus,
  FiSearch,
  FiMoreVertical,
  FiUsers,
  FiUser,
  FiLogOut,
  FiBookOpen,
  FiMessageSquare,
  FiLayout,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import "../styles/claudeTheme.css";
import "../styles/newChatPage.css";
import { routeStudent } from "../utils/studentRouting";
import "../styles/courseList_updated.css";
import PasswordResetModal from "./PasswordResetModal";

const CourseListPage = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [role, setRole] = useState("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverCourse, setPopoverCourse] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  // Modal states
  const [isFileManageModalVisible, setIsFileManageModalVisible] =
    useState(false);
  const [isJoinModalVisible, setIsJoinModalVisible] = useState(false);
  const [isRosterModalVisible, setIsRosterModalVisible] = useState(false);
  const [isCourseAddModalVisible, setIsCourseAddModalVisible] = useState(false);
  const [isTAManagementModalVisible, setIsTAManagementModalVisible] =
    useState(false);
  const [isLabSectionsModalVisible, setIsLabSectionsModalVisible] =
    useState(false);

  const [isPasswordResetModalVisible, setIsPasswordResetModalVisible] =
    useState(false);

  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const navigate = useNavigate();
  const { user, logout, refreshUser } = useContext(UserContext);
  const profileDropdownHideTimeout = useRef(null);
  const sessionValidatedRef = useRef(false);

  const hasManagerPermissions = () => {
    return user?.role === "teacher" || user?.effectiveRole === "manager";
  };

  useEffect(() => {
    const load = async () => {
      // Hydrate user from session when context user is null (e.g. right after login redirect)
      if (!user) {
        sessionValidatedRef.current = false;
        if (refreshUser) await refreshUser();
        return;
      }
      // Validate session before first courses fetch; then let effect re-run so we fetch courses once with a validated session (avoids race/double-fetch)
      if (!sessionValidatedRef.current && refreshUser) {
        await refreshUser();
        sessionValidatedRef.current = true;
        return;
      }
      fetchCourses();
      fetchUserRole();
    };
    load();
  }, [isCourseAddModalVisible, isJoinModalVisible, isRosterModalVisible, user]);

  useEffect(() => {
    const isManagerMode = hasManagerPermissions();
    if (role === "student" && courses.length === 1 && !isManagerMode) {
      routeStudent(navigate);
    }
  }, [role, courses]);

  useEffect(() => {
    const filtered = courses.filter((course) =>
      (course.course_number + " " + course.course_name)
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
    setFilteredCourses(filtered);
  }, [courses, searchQuery]);

  const fetchCourses = async () => {
    try {
      // Check if user is in TA manager mode from effectiveRole
      const isTAManagerMode = user?.effectiveRole === "manager";

      const response = await fetch(
        getApiUrl("/api/course/get"),
        PostOptions({
          ta_manager_mode: isTAManagerMode,
        })
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch courses");
      }
      setCourses(data.courses || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await axios.post(getApiUrl("/api/userinfo"));
      setRole(response.data.role);
    } catch (error) {}
  };

  const handleCourseClick = (course) => {
    localStorage.setItem("currentCourse", JSON.stringify(course));
    navigate(`/sessions/${course.course_number}/${course.course_name}`);
  };

  const togglePopover = (course, event) => {
    if (isPopoverOpen && popoverCourse === course) {
      setIsPopoverOpen(false);
      setPopoverCourse(null);
    } else {
      const buttonRect = event.target.getBoundingClientRect();
      const position = {
        top: buttonRect.bottom + window.scrollY + 8,
        left: buttonRect.left + window.scrollX - 200,
      };

      if (position.left < 10) {
        position.left = 10;
      }
      if (position.left + 320 > window.innerWidth) {
        position.left = window.innerWidth - 330;
      }

      setPopoverPosition(position);
      setPopoverCourse(course);
      setIsPopoverOpen(true);
    }
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
    setPopoverCourse(null);
  };

  const renderContextMenu = () => {
    const items = [
      {
        name: "Add/Remove Materials",
        onClick: (event) => {
          event.stopPropagation();
          setIsFileManageModalVisible(true);
          setIsPopoverOpen(false);
        },
      },
      {
        name: "Student Chat History",
        onClick: (event) => {
          event.stopPropagation();
          navigate(
            "/instructor/review/" +
              popoverCourse.course_number +
              "/" +
              popoverCourse.course_name
          );
          setIsPopoverOpen(false);
        },
      },
      {
        name: "Roster",
        onClick: (event) => {
          event.stopPropagation();
          setIsRosterModalVisible(true);
          setIsPopoverOpen(false);
        },
      },
      {
        name: "TA Management",
        onClick: (event) => {
          event.stopPropagation();
          if (popoverCourse) {
            setIsTAManagementModalVisible(true);
            setIsPopoverOpen(false);
          }
        },
      },
      {
        name: "Teachers Dashboard",
        onClick: (event) => {
          event.stopPropagation();
          if (popoverCourse) {
            navigate(
              `/teachers-dashboard/${popoverCourse.course_number}/${popoverCourse.course_name}`
            );
            setIsPopoverOpen(false);
          }
        },
      },
      {
        name: "Lab Sections",
        onClick: (event) => {
          event.stopPropagation();
          setIsLabSectionsModalVisible(true);
          setIsPopoverOpen(false);
        },
      },
      {
        name: "Forgot Password for Students",
        onClick: (event) => {
          event.stopPropagation();
          if (popoverCourse) {
            setIsPasswordResetModalVisible(true);
            setIsPopoverOpen(false);
          }
        },
      },
    ];

    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 16px",
            borderBottom: "1px solid #d3dae6",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: "14px" }}>Options</span>
          <button
            style={{
              minWidth: "auto",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "16px",
              padding: "4px",
              borderRadius: "4px",
            }}
            onClick={(event) => {
              event.stopPropagation();
              closePopover();
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "8px 0" }}>
          {items.map((item, index) => (
            <button
              key={index}
              className="menu-item"
              onClick={item.onClick}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "16px 24px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "14px",
                color: "#374151",
              }}
            >
              <span>{item.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isPopoverOpen &&
        !event.target.closest(".course-menu-popover-global") &&
        !event.target.closest(".course-menu-btn")
      ) {
        closePopover();
      }
    };

    if (isPopoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopoverOpen]);

  if (!user) {
    return <Blocked name="Course List" />;
  }

  return (
    <div className="claude-container full-page">
      {/* Header */}
      <header className="course-list-header">
        <div className="header-left">
          <div className="logo-section">
            <img
              src="/merryquery-logo.png"
              alt="MerryQuery"
              className="header-logo"
            />
            <span className="header-title">MerryQuery</span>
          </div>
        </div>

        <div className="header-center">
          <div className="search-container">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="header-right">
          <span className="course-action-tooltip-wrap">
            <button
              className="header-action-btn primary"
              onClick={() => setIsJoinModalVisible(true)}
            >
              <FiPlus />
              <span>Join Course</span>
            </button>
            <span className="header-tooltip">
              Join with an invite code. Your teacher shares a 6-digit code; enter it here to join the course.
            </span>
          </span>

          {hasManagerPermissions() && (
            <span className="course-action-tooltip-wrap">
              <button
                className="header-action-btn secondary"
                onClick={() => setIsCourseAddModalVisible(true)}
              >
                <FiBookOpen />
                <span>Create Course</span>
              </button>
              <span className="header-tooltip">
                Only teachers (and TAs in manager mode) can create courses. After creating one, share an invite code so students can join.
              </span>
            </span>
          )}

          <div
            className="user-profile profile-dropdown-container"
            onClick={(e) => {
              e.stopPropagation();
              setShowProfileDropdown((prev) => !prev);
            }}
          >
            <div className="user-avatar">
              <FiUser />
            </div>
            <div className="user-details">
              <span className="user-name">{user.full_name}</span>
              <span className="user-role">
                {user?.effectiveRole === "manager"
                  ? "TA (Manager)"
                  : role || user.role}
              </span>
            </div>

            {showProfileDropdown && (
              <div
                className="profile-dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  logout();
                  setShowProfileDropdown(false);
                }}
              >
                <FiLogOut />
                Log out
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="course-list-main">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <p>Loading your courses...</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FiBookOpen />
            </div>
            <h2>No courses found</h2>
            <p>
              {searchQuery
                ? `No courses match "${searchQuery}"`
                : hasManagerPermissions()
                ? "Create your first course or join an existing one"
                : "Join a course to get started"}
            </p>
            <div className="empty-state-actions">
              <span className="course-action-tooltip-wrap empty-state-tooltip-wrap">
                <button
                  className="action-btn primary"
                  onClick={() => setIsJoinModalVisible(true)}
                >
                  <FiPlus />
                  Join Course
                </button>
                <span className="header-tooltip">
                  Join with an invite code. Your teacher shares a 6-digit code; enter it here to join the course.
                </span>
              </span>
              {hasManagerPermissions() && (
                <span className="course-action-tooltip-wrap empty-state-tooltip-wrap">
                  <button
                    className="action-btn secondary"
                    onClick={() => setIsCourseAddModalVisible(true)}
                  >
                    <FiBookOpen />
                    Create Course
                  </button>
                  <span className="header-tooltip">
                    Only teachers (and TAs in manager mode) can create courses. After creating one, share an invite code so students can join.
                  </span>
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="courses-grid">
            {filteredCourses.map((course, index) => (
              <div
                key={`${course._id || course.id}-${course.course_name}`}
                className="course-card"
                onClick={() => handleCourseClick(course)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="course-card-header">
                  {course.image_url && (
                    <div className="course-image">
                      <img src={course.image_url} alt={course.course_name} />
                    </div>
                  )}
                  <div className="course-info">
                    <h3 className="course-title">
                      {course.course_number} {course.course_name}
                    </h3>
                    {course.course_description && (
                      <>
                        <p
                          className={`course-description ${
                            expandedDescriptions[
                              `${course.course_number}|${course.course_name}`
                            ]
                              ? "course-description-expanded"
                              : "course-description-collapsed"
                          }`}
                        >
                          {course.course_description}
                        </p>
                        {course.course_description.length > 100 && (
                          <button
                            type="button"
                            className="course-description-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              const key = `${course.course_number}|${course.course_name}`;
                              setExpandedDescriptions((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }}
                          >
                            {expandedDescriptions[
                              `${course.course_number}|${course.course_name}`
                            ] ? (
                              <>
                                Show less
                                <FiChevronUp />
                              </>
                            ) : (
                              <>
                                Show more
                                <FiChevronDown />
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {hasManagerPermissions() && (
                    <button
                      className="course-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePopover(course, e);
                      }}
                    >
                      <FiMoreVertical />
                    </button>
                  )}
                </div>

                <div className="course-card-footer">
                  <div className="course-stats">
                    <div className="stat">
                      <FiUsers />
                      <span>Students</span>
                    </div>
                  </div>
                  <div className="course-card-actions">
                    {hasManagerPermissions() && (
                      <button
                        className="chat-btn dashboard-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          localStorage.setItem("currentCourse", JSON.stringify(course));
                          navigate(
                            `/teachers-dashboard/${course.course_number}/${course.course_name}`
                          );
                        }}
                        title="Teacher Dashboard"
                      >
                        <FiLayout />
                        <span>Dashboard</span>
                      </button>
                    )}
                    <button
                      className="chat-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCourseClick(course);
                      }}
                    >
                      <FiMessageSquare />
                      <span>Chat</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modals */}
      {popoverCourse && (
        <>
          <FileManagePage
            course={popoverCourse}
            isModalVisible={isFileManageModalVisible}
            setIsModalVisible={setIsFileManageModalVisible}
          />
          <RosterPage
            isModalVisible={isRosterModalVisible}
            setIsModalVisible={setIsRosterModalVisible}
            course={popoverCourse}
          />
          <TAManagementDialog
            isModalVisible={isTAManagementModalVisible}
            setIsModalVisible={setIsTAManagementModalVisible}
            course={popoverCourse}
          />
          <LabSectionConfigModal
            isModalVisible={isLabSectionsModalVisible}
            setIsModalVisible={setIsLabSectionsModalVisible}
          />
          <PasswordResetModal
            isModalVisible={isPasswordResetModalVisible}
            setIsModalVisible={setIsPasswordResetModalVisible}
            course={popoverCourse}
            getApiUrl={getApiUrl}
            PostOptions={PostOptions}
          />
        </>
      )}

      {/* Global Popover Menu */}
      {isPopoverOpen && popoverCourse && (
        <div
          className="course-menu-popover-global"
          style={{
            position: "fixed",
            top: popoverPosition.top,
            left: popoverPosition.left,
            zIndex: 10000,
            background: "white",
            borderRadius: "16px",
            minWidth: "280px",
            maxWidth: "320px",
            boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {renderContextMenu()}
        </div>
      )}

      <JoinPage
        isModalVisible={isJoinModalVisible}
        setIsModalVisible={setIsJoinModalVisible}
      />

      {hasManagerPermissions() && (
        <CourseAddPage
          isModalVisible={isCourseAddModalVisible}
          setIsModalVisible={setIsCourseAddModalVisible}
        />
      )}
    </div>
  );
};

export default CourseListPage;
